import React, { FunctionComponent, useCallback, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { ColDef, ColGroupDef, GridApi, GridOptions } from 'ag-grid-community'
import cx from 'classnames'
import {
  DefaultCollateralIdentifierType,
  EntityType,
  EntityTypeEnum,
  NegotiationActions,
  NegotiationDetail,
  NegotiationDetailWithMeta,
  PlatformFeature,
  RfqType,
  SubscriptionIdentifier
} from 'api'
import { CenterPanel } from 'view/routes/routesLayout'
import { InitiatedNegotiationsHeroPanel } from './InitiatedNegotiationsHeroPanel'
import { DataGrid, filteredColumnDefsBuilder, GridAutoSizing, GridId, GroupRowData } from 'view/components/datagrid'
import { BestPriceTypeSelectbox } from 'view/components/datagrid/toolbar/BestPriceTypeSelectbox'
import { CancelAllNegotiationsButton } from 'view/components/datagrid/toolbar/CancelAllNegotiationsButton'
import { DeltaToolbarButton } from '../LiveNegotiationsGrid/DeltaToolbarButton'
import { InitiatedNegotiationsToolbarSwitch } from './InitiatedNegotiationsToolbarSwitch'
import { OrganisationFilterSwitch } from 'view/components/datagrid/toolbar/OrganisationFilterSwitch'
import { negotiationRowDataGetter } from 'view/shared/datagrid'
import { LiveGridColumnId, NegotiationFieldNames } from '../LiveNegotiationsGrid/LiveGridColumnNames'
import { CollectionColumnId, ContainerCollectionColumnId } from 'view/routes/ColumnNames'
import { RepositoryActions } from 'store/repository'
import { shouldStartEdit } from 'view/routes/requests/shared/utils'
import { primitive, sliceFieldName } from 'utils'
import { SubscriptionDetail } from 'store/selection/selectionTypes'
import {
  initiatedColumnDefsSelector,
  initiatedNegotiationsGridOptions,
  createUserInitiatedNegotiationsSelector,
  userInitiatedSummaryNegotiationsMapSelector
} from 'selectors/negotiations'

import {
  createIsInitiatedNegotiationCounterpartyViewEnabled,
  defaultCollateralIdentifierTypeSelector
} from 'selectors/userPreferenceSelectors'
import { createEntitySnapshotLoadedSelector } from 'selectors/systemSelectors'
import { createPlatformFeatureEnabledSelector } from 'selectors/platformSelectors'
import { createEntitySubscriptionSelector, createSelectedCollectionIdSelector } from 'selectors/selectionSelectors'
import { startConsiderationOverrideEnabledSelector, isStockBorrowingLendingEnabledSelector } from 'selectors/featureSelectors'
import { InitiatedGroupByOptions, InitiatedGroupByToggles } from './InitiatedGroupByToggles'
import { orderBy } from 'lodash'
import { ModelUpdatedEvent } from 'ag-grid-community'
import { BulkPriceInput } from '../incoming/BulkPriceInput'
import { generateExportFileName } from 'view/components/datagrid/gridUtils/utils'
import { isAdminGatewayUserSelector } from 'selectors/userSelectors'

interface InitiatedNegotiationsScreenProps {
  gridOptions: GridOptions
  columnDefs: (ColGroupDef | ColDef)[]
  currentNegotiations: NegotiationDetailWithMeta[]
  evergreenNoticeEnabled: boolean
  summaryNegotiationsMap: GroupRowData<NegotiationDetailWithMeta> | undefined
  subscriptionDetail?: SubscriptionDetail
  isCounterpartyViewEnabled: boolean
  snapshotLoaded: boolean
  allowPartialEnabled: boolean
  defaultCollateralIdentifierType: DefaultCollateralIdentifierType
  onPageSubscribe: typeof NegotiationActions.pageSubscribe
  onUpdateField: typeof RepositoryActions.updateField
  startConsiderationOverrideEnabled: boolean
  stockBorrowingLendingEnabled: boolean
  selectedCollectionId: string | null
  isAdmin: boolean
}

const mapStateToProps = createStructuredSelector({
  gridOptions: initiatedNegotiationsGridOptions,
  columnDefs: initiatedColumnDefsSelector,
  currentNegotiations: createUserInitiatedNegotiationsSelector,
  summaryNegotiationsMap: userInitiatedSummaryNegotiationsMapSelector,
  subscriptionDetail: createEntitySubscriptionSelector(
    () => EntityTypeEnum.NEGOTIATION,
    () => SubscriptionIdentifier.INITIATED
  ),
  isCounterpartyViewEnabled: createIsInitiatedNegotiationCounterpartyViewEnabled(),
  snapshotLoaded: createEntitySnapshotLoadedSelector(EntityTypeEnum.NEGOTIATION),
  evergreenNoticeEnabled: createPlatformFeatureEnabledSelector(PlatformFeature.ENABLE_EVERGREENS),
  allowPartialEnabled: createPlatformFeatureEnabledSelector(PlatformFeature.ALLOW_PARTIAL),
  defaultCollateralIdentifierType: defaultCollateralIdentifierTypeSelector,
  selectedCollectionId: createSelectedCollectionIdSelector(GridId.InitiatedGrid),
  startConsiderationOverrideEnabled: startConsiderationOverrideEnabledSelector,
  stockBorrowingLendingEnabled: isStockBorrowingLendingEnabledSelector,
  isAdmin: isAdminGatewayUserSelector
})

const mapDispatchToProps = {
  onPageSubscribe: NegotiationActions.pageSubscribe,
  onUpdateField: RepositoryActions.updateField
}

export const InitiatedNegotiationsScreenComponent: FunctionComponent<InitiatedNegotiationsScreenProps> = props => {
  const {
    columnDefs,
    gridOptions,
    currentNegotiations,
    evergreenNoticeEnabled,
    summaryNegotiationsMap,
    subscriptionDetail,
    selectedCollectionId,
    isCounterpartyViewEnabled,
    snapshotLoaded,
    allowPartialEnabled,
    defaultCollateralIdentifierType,
    onPageSubscribe,
    onUpdateField,
    startConsiderationOverrideEnabled,
    stockBorrowingLendingEnabled,
    isAdmin
  } = props

  const someNegotiationsHaveCollection = useMemo(
    () => currentNegotiations.some((negotiationDetail: NegotiationDetail) => negotiationDetail.collection),
    [currentNegotiations]
  )

  const filteredColumnDefs = useMemo(
    () =>
      filteredColumnDefsBuilder(columnDefs)
        .withoutIf(!evergreenNoticeEnabled, [LiveGridColumnId.EVERGREEN_NOTICE_PERIOD])
        .withoutIf(!allowPartialEnabled, [
          LiveGridColumnId.ALLOW_PARTIAL,
          LiveGridColumnId.NEGOTIATION_TOTAL_FILL_NOTIONAL,
          LiveGridColumnId.NEGOTIATION_TARGET_FILL_NOTIONAL,
          LiveGridColumnId.NEGOTIATION_TOTAL_FILL_START_CONSIDERATION,
          LiveGridColumnId.NEGOTIATION_TARGET_FILL_START_CONSIDERATION
        ])
        .withoutIf(!isAdmin, [LiveGridColumnId.MY_FICC_ENTITY_SHORT_NAME])
        .withoutIf(!startConsiderationOverrideEnabled, [LiveGridColumnId.NEGOTIATION_START_CONSIDERATION_OVERRIDE])
        .withoutIf(!stockBorrowingLendingEnabled, [
          LiveGridColumnId.SECONDARY_COLLATERAL_NAME,
          LiveGridColumnId.NEGOTIATION_STOCK_BORROWING_LENDING_TYPE,
          LiveGridColumnId.NEGOTIATION_FEE
        ])
        .withColIdSelector(colId => colId.cellRendererParams?.originalColId)
        .build(),
    [allowPartialEnabled, columnDefs, evergreenNoticeEnabled, startConsiderationOverrideEnabled, stockBorrowingLendingEnabled, isAdmin]
  )

  const handleCellValueUpdated = (
    negotiationId: string,
    _negotiationDetail: NegotiationDetail,
    field: string,
    value: primitive,
    rawValue?: primitive
  ) => {
    const fieldName = sliceFieldName(field, 1)
    onUpdateField(EntityType.NEGOTIATION, negotiationId, fieldName, value, rawValue, { ownOrder: true })
  }

  const lockedPrimaryRowGroupColumnIds = isCounterpartyViewEnabled
    ? someNegotiationsHaveCollection
      ? [CollectionColumnId.COLLECTION_NAME]
      : [LiveGridColumnId.NEGOTIATION_ORDER_ID]
    : undefined

  const shouldRedrawOnCellValueUpdate = (field: string, rowItem: NegotiationDetail) =>
    [
      NegotiationFieldNames.NOTIONAL_MODIFIED,
      NegotiationFieldNames.START_CONSIDERATION_MODIFIED,
      LiveGridColumnId.NEGOTIATION_REPO_TYPE
    ].includes(field) && rowItem.negotiation.rfqType.negotiated === RfqType.INDICATIVE

  const shouldStopEditOnRowDataUpdate = (
    previousNegotiationDetail: NegotiationDetail | null,
    newNegotiationDetail: NegotiationDetail | null
  ) => {
    return previousNegotiationDetail?.negotiation?.status !== newNegotiationDetail?.negotiation?.status
  }

  const handleAfterRestoreDefaultLayout = useCallback(
    (gridApi: GridApi) => {
      if (!isCounterpartyViewEnabled && someNegotiationsHaveCollection) {
        gridApi.setColumnsVisible([CollectionColumnId.COLLECTION_NAME], true)
        gridApi.setRowGroupColumns([ContainerCollectionColumnId.CONTAINER_COLLECTION_NAME, CollectionColumnId.COLLECTION_NAME])
      }

      gridApi.setColumnsVisible(
        [LiveGridColumnId.COLLATERAL_ISIN_OR_BUI],
        defaultCollateralIdentifierType !== DefaultCollateralIdentifierType.CUSIP
      )

      gridApi.setColumnsVisible(
        [LiveGridColumnId.COLLATERAL_CUSIP],
        defaultCollateralIdentifierType !== DefaultCollateralIdentifierType.ISIN
      )
    },
    [defaultCollateralIdentifierType, isCounterpartyViewEnabled, someNegotiationsHaveCollection]
  )

  const onPageChanged = useCallback(
    (pageNumber: number) => {
      if (subscriptionDetail) {
        onPageSubscribe(SubscriptionIdentifier.INITIATED, subscriptionDetail.filters, pageNumber)
      }
    },
    [onPageSubscribe, subscriptionDetail]
  )

  const [shouldSort, setShouldSort] = useState(false)

  const handleFilterChange = (groupBy: InitiatedGroupByOptions) => {
    groupBy === InitiatedGroupByOptions.COLLECTIONS_COUNTERPARTY ? setShouldSort(true) : setShouldSort(false)
  }

  //This is used to force a redraw on group rows by assigning the node data back to itself
  //Currently group row data is taken directly from the store therefore it does not refresh on value change in the grid
  const setGroupNodeDataToRedrawRow = useCallback((e: ModelUpdatedEvent) => {
    e.api
      .getRenderedNodes()
      .filter(n => n.group)
      .map(r => r.setData(r.data))
  }, [])

  const rowData = useMemo(() => {
    const filterNegotiationsByRowIndex = (negotiationDetails: NegotiationDetailWithMeta[]) => {
      return orderBy(negotiationDetails, ['negotiation.rfqEndTime', 'negotiation.uploadRowIndex'], ['asc', 'asc'])
    }

    return shouldSort ? filterNegotiationsByRowIndex(currentNegotiations) : currentNegotiations
  }, [currentNegotiations, shouldSort])

  return (
    <CenterPanel>
      <InitiatedNegotiationsHeroPanel />
      <DataGrid
        key={isCounterpartyViewEnabled ? `${GridId.InitiatedGridCounterparty}/${selectedCollectionId}` : GridId.InitiatedGrid}
        disableGrouping={isCounterpartyViewEnabled}
        gridColumnAutoSizing={GridAutoSizing.IGNORE_HEADERS}
        gridId={isCounterpartyViewEnabled ? GridId.InitiatedGridCounterparty : GridId.InitiatedGrid}
        gridClass={cx('live-grid', { 'live-grid--pivoted-view': isCounterpartyViewEnabled })}
        gridOptions={gridOptions}
        columnDefs={filteredColumnDefs}
        rowData={rowData}
        groupRowData={summaryNegotiationsMap}
        customToolbarButtons={[DeltaToolbarButton]}
        customToolbarSwitches={[
          CancelAllNegotiationsButton,
          BestPriceTypeSelectbox,
          OrganisationFilterSwitch,
          InitiatedNegotiationsToolbarSwitch
        ]}
        customToolbarComponents={isCounterpartyViewEnabled ? undefined : [BulkPriceInput]}
        GroupByToggles={
          isCounterpartyViewEnabled
            ? undefined
            : ({ gridApi }) => <InitiatedGroupByToggles gridApi={gridApi} handleFilterChange={handleFilterChange} />
        }
        groupDefaultExpanded={gridOptions.groupDefaultExpanded}
        lockedPrimaryRowGroupColumnIds={lockedPrimaryRowGroupColumnIds}
        forceLockedPrimaryRowGroupColumns={isCounterpartyViewEnabled}
        editableRowGroupColumnConfig={{
          [isCounterpartyViewEnabled ? CollectionColumnId.COLLECTION_NAME : LiveGridColumnId.COUNTERPARTY_NAME]: negotiationRowDataGetter
        }}
        canEditRow={shouldStartEdit}
        trackGridRowInEdit={true}
        showLoader={!snapshotLoaded}
        subscriptionDetail={subscriptionDetail}
        onPageChanged={onPageChanged}
        shouldRedrawOnCellValueUpdate={shouldRedrawOnCellValueUpdate}
        shouldStopEditOnRowDataUpdate={shouldStopEditOnRowDataUpdate}
        onCellValueUpdated={handleCellValueUpdated}
        onAfterRestoreDefaultLayout={handleAfterRestoreDefaultLayout}
        onModelUpdated={setGroupNodeDataToRedrawRow}
        exportFileName={generateExportFileName(isCounterpartyViewEnabled ? 'Initiated_Counterparty' : 'Initiated_Default')}
      />
    </CenterPanel>
  )
}

export const InitiatedNegotiationsScreen = connect(mapStateToProps, mapDispatchToProps)(InitiatedNegotiationsScreenComponent)
